import {callAPI} from '@/store/utils'; // eslint-disable-line
import {base} from './utils';

// Posting rules & resolution config: which account generated entries post
// against. Backs "Comptes auxiliaires" (counterparty 411/401 defaults),
// "Mapping" (category targets) and "Règles → Comptable". Composed into the
// `accounting` namespace by ./index.js.
export default {
  state: {
    // posting_rule rows — anchored on a category and/or counterparty.
    postingRules: [],
    postingRulesLoaded: false,
    // categorization_rule_posting rows — accounting extension anchored on a
    // categorization rule (the "inherited from a rule" side of Règles→Comptable).
    categorizationRulePostings: [],
    categorizationRulePostingsLoaded: false,
  },
  getters: {
    postingRules: (state) => state.postingRules,
    postingRulesLoaded: (state) => state.postingRulesLoaded,
    categorizationRulePostings: (state) => state.categorizationRulePostings,
    categorizationRulePostingsLoaded: (state) =>
      state.categorizationRulePostingsLoaded,
    // Counterparty-anchored rules only (counterparty set, no category) — the
    // single 411/401 default per counterparty behind "Comptes auxiliaires".
    counterpartyPostingRules: (state) =>
      (state.postingRules || []).filter(
        (r) => r.counterparty_id && !r.category_id,
      ),
    // Category-anchored rules only (category set, no counterparty) — the
    // Achats/Banque targets per category behind "Mapping".
    categoryPostingRules: (state) =>
      (state.postingRules || []).filter(
        (r) => r.category_id && !r.counterparty_id,
      ),
  },
  mutations: {
    setPostingRules(state, list) {
      state.postingRules = list || [];
      state.postingRulesLoaded = true;
    },
    setCategorizationRulePostings(state, list) {
      state.categorizationRulePostings = list || [];
      state.categorizationRulePostingsLoaded = true;
    },
    // Local upsert/remove so a single mutation doesn't force a full refetch of
    // both lists.
    upsertPostingRule(state, rule) {
      if (!rule) return;
      const i = state.postingRules.findIndex((r) => r.id === rule.id);
      if (i === -1) state.postingRules.push(rule);
      else state.postingRules.splice(i, 1, rule);
    },
    removePostingRuleById(state, id) {
      state.postingRules = state.postingRules.filter((r) => r.id !== id);
    },
    // Remove the single counterparty-/category-anchored rule (used when an
    // upsert cleared its last account and the backend dropped the row).
    removePostingRuleByCounterparty(state, counterpartyId) {
      state.postingRules = state.postingRules.filter(
        (r) => !(r.counterparty_id === counterpartyId && !r.category_id),
      );
    },
    removePostingRuleByCategory(state, categoryId) {
      state.postingRules = state.postingRules.filter(
        (r) => !(r.category_id === categoryId && !r.counterparty_id),
      );
    },
    upsertCategorizationRulePosting(state, rp) {
      if (!rp) return;
      const i = state.categorizationRulePostings.findIndex(
        (x) => x.id === rp.id,
      );
      if (i === -1) state.categorizationRulePostings.push(rp);
      else state.categorizationRulePostings.splice(i, 1, rp);
    },
    removeCategorizationRulePostingByRuleId(state, ruleId) {
      state.categorizationRulePostings =
        state.categorizationRulePostings.filter(
          (x) => x.categorization_rule_id !== ruleId,
        );
    },
  },
  actions: {
    async actionGetPostingRules({commit}) {
      const res = await callAPI('GET', `${base()}/posting-rules`);
      if (res && !res.error) commit('setPostingRules', res.result);
      return res;
    },
    // All categorization_rule_posting rows in one call — the "inherited from a
    // rule" rows of Règles→Comptable.
    async actionGetCategorizationRulePostings({commit}) {
      const res = await callAPI(
        'GET',
        `${base()}/categorization-rule-postings`,
      );
      if (res && !res.error) commit('setCategorizationRulePostings', res.result);
      return res;
    },
    // Comptes auxiliaires: idempotent upsert of the single counterparty-anchored
    // rule. body: { receivable_account_id?, payable_account_id? } (411 / 401).
    // Patches the store locally (no refetch).
    async actionSetCounterpartyAccounts({commit}, {counterpartyId, ...body}) {
      const res = await callAPI(
        'PUT',
        `${base()}/counterparties/${counterpartyId}/accounts`,
        body,
      );
      if (res && !res.error) {
        if (res.result) commit('upsertPostingRule', res.result);
        else commit('removePostingRuleByCounterparty', counterpartyId);
      }
      return res;
    },
    // Mapping: upsert the single category-anchored rule. body may include any of
    // the target fields (bank_journal_target_account_id,
    // purchase_journal_target_account_id, …). Patches the store locally.
    async actionSetCategoryTargets({commit}, {categoryId, ...body}) {
      const res = await callAPI(
        'PUT',
        `${base()}/categories/${categoryId}/targets`,
        body,
      );
      if (res && !res.error) {
        if (res.result) commit('upsertPostingRule', res.result);
        else commit('removePostingRuleByCategory', categoryId);
      }
      return res;
    },
    // Règles → Comptable, MANUEL side: standalone posting_rule CRUD. body needs
    // an anchor (category_id and/or counterparty_id) + ≥1 target account. On
    // success the store is patched locally (no refetch).
    async actionCreatePostingRule({commit}, body) {
      // Client-generated id (app convention) → optimistic add before the
      // round-trip; reconcile on success, roll back on error.
      const optimistic = {id: window.utils.genId(), ...body};
      commit('upsertPostingRule', optimistic);
      const res = await callAPI('POST', `${base()}/posting-rules`, optimistic);
      if (res && !res.error) commit('upsertPostingRule', res.result);
      else commit('removePostingRuleById', optimistic.id);
      return res;
    },
    async actionUpdatePostingRule({commit}, {id, ...body}) {
      const res = await callAPI('PATCH', `${base()}/posting-rules/${id}`, body);
      if (res && !res.error) commit('upsertPostingRule', res.result);
      return res;
    },
    async actionDeletePostingRule({commit}, {id}) {
      const res = await callAPI('DELETE', `${base()}/posting-rules/${id}`);
      if (res && !res.error) commit('removePostingRuleById', id);
      return res;
    },
    // Règles → Comptable, RÈGLE side: the accounting extension of a
    // categorization rule. PUT upserts (≥1 target); DELETE removes only the
    // extension (the rule keeps categorizing).
    async actionSetCategorizationRulePosting({commit}, {ruleId, ...body}) {
      const res = await callAPI(
        'PUT',
        `${base()}/categorization-rules/${ruleId}/posting`,
        body,
      );
      if (res && !res.error) commit('upsertCategorizationRulePosting', res.result);
      return res;
    },
    async actionDeleteCategorizationRulePosting({commit}, {ruleId}) {
      const res = await callAPI(
        'DELETE',
        `${base()}/categorization-rules/${ruleId}/posting`,
      );
      if (res && !res.error)
        commit('removeCategorizationRulePostingByRuleId', ruleId);
      return res;
    },
  },
};
